import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import useSharePointPdf from "./useSharePointPdf";

/**
 * Convert a SharePoint share/edit URL into its frameable "embed view" form.
 * SharePoint (and its login page) blocks normal iframes via X-Frame-Options,
 * so the `action=embedview` variant must be used to embed a file.
 */
export const toSharePointEmbedUrl = (url) => {
  if (!url || !/sharepoint\.com/i.test(url)) return url;
  try {
    const u = new URL(url);
    const params = new URLSearchParams(u.search);
    if (
      u.pathname.includes("_layouts") ||
      u.pathname.includes("Doc.aspx") ||
      /\/:[bwip]:\//.test(u.pathname)
    ) {
      params.set("action", "embedview");
      u.search = params.toString();
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
};

/**
 * Reusable, configurable iframe-based PDF viewer.
 *
 * `src` accepts either a direct http(s) URL or a blob/object URL
 * (e.g. URL.createObjectURL(blob)) — the iframe renders both the same way.
 */
const PdfViewer = ({
  src,
  title = "PDF Document",
  height = "80vh",
  width = "100%",
  useProxy = false,
}) => {
  // When proxying, resolve `src` to a blob URL fetched via the backend.
  const { blobUrl, loading, error } = useSharePointPdf(
    useProxy ? src : null,
  );
  const resolvedSrc = blobUrl || toSharePointEmbedUrl(src);

  if (!src) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No document available.</Typography>
      </Box>
    );
  }

  if (useProxy && loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (useProxy && error) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "error.main" }}>
        <Typography variant="body2">
          Failed to load document. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={resolvedSrc}
      title={title}
      sx={{ width, height, border: "none", display: "block" }}
    />
  );
};

export default PdfViewer;
